import { useMemo } from 'react';

import { useDataProvider } from '@work4all/data';

import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { usePositionDone, UsePositionDoneOptions } from './use-position-done';

interface UsePositionDoneWithDataOptions
  extends Omit<UsePositionDoneOptions, 'contract'> {
  contractId?: string | number;
}

export const usePositionDoneWithData = (
  options?: UsePositionDoneWithDataOptions
) => {
  const { contractId, ...restOptions } = options ?? {};
  // Define the contract data fields we need
  const contractRequestData = useMemo<DataRequest>(() => {
    return {
      entity: Entities.contract,
      data: CONTRACT_FIELDS,
      filter: contractId ? [{ id: { $eq: contractId } }] : [],
    };
  }, [contractId]);

  // Fetch contract data using useDataProvider
  const {
    data: contractData,
    pending,
    refetch,
  } = useDataProvider<Contract>(
    contractRequestData,
    !contractId // skip if no contractId provided
  );

  const contract = contractData?.[0];

  // Pass the fetched contract data to usePositionDone
  const actions = usePositionDone({
    ...restOptions,
    contract,
  });

  return {
    actions,
    contract,
    pending,
    refetch,
  };
};

// Define the contract fields needed for the position done functionality
const CONTRACT_FIELDS: Contract<EMode.query> = {
  id: null,
  number: null,
  contractNumber: null,
  contractDate: null,
  note: null,
  isClosed: null,
  user: {
    id: null,
    displayName: null,
  },
  positionList: [
    {
      id: null,
      index: null,
      number: null,
      shortText: null,
      longtext: null,
      positionKind: null,
      amount: null,
      unit: null,
      isFinished: null,
      finishedDate: null,
      finishedByUserId: null,
      finishedNote: null,
      article: {
        id: null,
        name: null,
        notdeductable: null,
        isActivity: null,
      },
    },
  ],
  businessPartnerId: null,
  businessPartnerType: null,
  businessPartnerContactCombined: {
    businessPartner: {
      id: null,
      data: {
        customer: {
          id: null,
          name: null,
          number: null,
          website: null,
        },
      },
    },
  },
  project: {
    id: null,
    name: null,
    number: null,
  },
  projectProcess: {
    id: null,
    process: null,
  },
};
